import { chakra } from "@chakra-ui/react";
import { DevTool } from "@hookform/devtools";
import React, { useImperativeHandle } from "react";
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  UseFormReturn,
} from "react-hook-form";
import { useFormNavigationBlocker } from "shared/components/form/FormNavBlocker";
import { FormNavConfirmationDialog } from "shared/components/form/FormNavConfirmationDialog";
import { FormInteractionStateProvider } from "shared/components/form/FormStateProvider";
import {
  createFormSubmitEvent,
  FormSubmitEventDetails,
} from "shared/components/form/FormSubmitEvent";
import { FormOnErrorFn } from "shared/components/form/FormTypes";
import { useFormSubmitWrapper } from "shared/components/form/UseFormSubmitWrapper";
import { useDetectRenderLoop } from "shared/utils/ReactUtils";
import { useReadLocalStorage } from "usehooks-ts";

export interface FormHandle {
  submit: (details?: FormSubmitEventDetails) => Promise<boolean>;
}

export interface FormProps<T extends FieldValues, R> {
  form: UseFormReturn<T>;
  // NOTE: Type inference for onSuccess doesn't always work due to:
  // https://github.com/microsoft/TypeScript/issues/50121
  onSubmit: (values: T) => R | Promise<R>;
  onSuccess?: (data: R, values: T) => void | Promise<void>;
  onSaveProgress?: (values: T) => R | Promise<R>;
  onBlur?: React.FormEventHandler<HTMLFormElement>;
  onError?: FormOnErrorFn<T>;
  onBeforeSubmit?: () => void;
  successMessage?: string;
  objectDisplayName?: string;
  disableNavBlocker?: boolean;
  formRef?: React.RefObject<FormHandle>;
  excludeNonFieldErrors?: (keyof FieldErrors<T>)[];
  isDisabled?: boolean;
  disableWhileSubmitting?: boolean;
}

const Form = <T extends FieldValues, R>({
  children,
  form,
  onSubmit,
  onSuccess,
  onSaveProgress,
  onBlur,
  onError,
  onBeforeSubmit,
  successMessage,
  objectDisplayName,
  disableNavBlocker,
  excludeNonFieldErrors = [],
  isDisabled = false,
  formRef,
  disableWhileSubmitting = true,
}: React.PropsWithChildren<FormProps<T, R>>) => {
  useDetectRenderLoop();

  const showDevTools = useReadLocalStorage<boolean>("show-dev-tools");

  const { isSubmitting } = form.formState;

  const blocker = useFormNavigationBlocker(form, disableNavBlocker);

  const onSubmitWrapper = useFormSubmitWrapper({
    form,
    onSubmit,
    onSuccess,
    onSaveProgress,
    onError,
    onBeforeSubmit,
    successMessage,
    excludeNonFieldErrors,
  });

  useImperativeHandle(formRef, () => ({
    submit: ({ skipOnSuccess }: FormSubmitEventDetails = {}) =>
      onSubmitWrapper(createFormSubmitEvent({ skipOnSuccess })),
  }));

  return (
    <FormProvider<T> {...form}>
      <FormInteractionStateProvider
        isDisabled={isDisabled || (disableWhileSubmitting && isSubmitting)}
      >
        {showDevTools && <DevTool control={form.control} />}
        <chakra.form
          display="contents"
          onSubmit={onSubmitWrapper}
          onBlur={onBlur}
        >
          {children}
        </chakra.form>
        <FormNavConfirmationDialog
          objectDisplayName={objectDisplayName}
          onSubmit={onSubmitWrapper}
          isSubmitting={form.formState.isSubmitting}
          blocker={blocker}
        />
      </FormInteractionStateProvider>
    </FormProvider>
  );
};

export default Form;
